<template>
  <vee-form v-slot="{ validate }" as="" :initial-errors="initialErrors">
    <form
      ref="formRef"
      :class="formClasses"
      method="post"
      :action="formAction"
      novalidate
      @submit.prevent="handleSubmit(validate)"
    >
      <div :class="showAside ? 'registration' : ''">
        <div :class="showAside ? 'form__main' : ''">
          <text-input
            v-model="formData.first_name"
            input-id="first_name"
            label="First Name"
            :show-label="false"
            placeholder="First Name"
            rules="required|max:50"
            validate-as="First name"
            maxlength="50"
          />

          <text-input
            v-model="formData.last_name"
            input-id="last_name"
            label="Last Name"
            :show-label="false"
            placeholder="Last Name"
            rules="required|max:50"
            validate-as="Last name"
            maxlength="50"
          />

          <text-input
            v-model="formData.email"
            input-id="email"
            type="email"
            label="Email"
            :show-label="false"
            placeholder="Email Address"
            rules="required|email|max:100|fbEmailUnique"
            autocomplete="email"
            :validate-aggressive="false"
            maxlength="100"
          />

          <text-input
            v-model="formData.username"
            input-id="username"
            label="Username"
            :show-label="false"
            placeholder="Username"
            rules="required|min:4|max:50|alpha_dash:en|fbUsername|fbUsernameUnique"
            autocomplete="username"
            :validate-aggressive="false"
            maxlength="50"
          />

          <text-input
            v-model="formData.password"
            input-id="password"
            type="password"
            label="Password"
            :show-label="false"
            placeholder="Password"
            rules="required|min:12|fbNotSameAs:username|fbPasswordStrengthRule"
            autocomplete="new-password"
            :validate-aggressive="false"
          />

          <text-input
            v-model="formData.password_confirmation"
            input-id="password_confirmation"
            type="password"
            label="Confirm Password"
            :show-label="false"
            placeholder="Confirm Password"
            rules="required|confirmed:@password,password"
            autocomplete="new-password"
          />
        </div>

        <aside v-if="showAside" class="form__sidebar">
          <h3>Key Benefits:</h3>
          <ul class="check-list">
            <li><span class="iconfont-check-small"></span>Customizable fitness calendar</li>
            <li><span class="iconfont-check-small"></span>Daily progress tracking</li>
            <li><span class="iconfont-check-small"></span>Favorite workout videos</li>
            <li><span class="iconfont-check-small"></span>24/7 community support</li>
            <li>
              <span class="iconfont-check-small"></span>Helpful health, nutrition and fitness
              information
            </li>
            <li><span class="iconfont-check-small"></span>and much, much more…</li>
          </ul>
        </aside>

        <!-- additional form inputs -->
        <h-captcha
          :error="captchaError"
          @verify="captchaVerify"
          @expired="captchaExpired"
          @error="captchaExpired"
        />

        <checkbox-input
          v-model="formData.tos"
          input-id="tos"
          checked-value="agree"
          unchecked-value=""
          rules="required"
          validate-as="Terms of service"
        >
          <template #label>
            <span>
              I agree to the Fitness Blender
              <a href="/page/terms-of-use-agreement" target="_blank" rel="noopener noreferrer"
                >terms of service</a
              >
              and
              <a href="/page/privacy-policy" target="_blank" rel="noopener noreferrer"
                >privacy policy</a
              >
            </span>
          </template>
        </checkbox-input>

        <!-- additional form fields -->
        <div :class="showAside ? 'form__actions' : 'form-actions'">
          <csrf-input />

          <div class="form-action">
            <submit-button
              :submitting="processing"
              :label="buttonText"
              submitting-label="Processing…"
              classes="-main"
              @submit-action="handleSubmit(validate)"
            />
          </div>

          <p v-if="!showTosInput" class="medium light">
            By joining, you agree to the Fitness Blender
            <a href="/page/terms-of-use-agreement">Terms of Service</a> and
            <a href="/page/privacy-policy">Privacy Policy</a>
          </p>
        </div>
      </div>
    </form>
  </vee-form>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { Form as VeeForm, type PrivateFormContext } from 'vee-validate'
import { initExtraValidationRules, errorBagToInitialErrors } from '@/utils/forms/validation-rules'
import TextInput from '@/components/forms/text-input.vue'
import CsrfInput from '@/components/forms/csrf-input.vue'
import SubmitButton from '@/components/buttons/submit-button.vue'
import HCaptcha from '@/components/forms/hcaptcha.vue'
import CheckboxInput from '@/components/forms/checkbox-input.vue'

/**
 * "blender" auth registration from
 * - if showAside, lots of container classes change to accommodate /join styling
 * - otherwise see organization join, which is much more simple
 *
 * Side effect is empty div container classes
 */
export default defineComponent({
  name: 'BlenderRegisterForm',
  components: {
    CheckboxInput,
    VeeForm,
    TextInput,
    CsrfInput,
    SubmitButton,
    HCaptcha,
  },
  props: {
    errorBag: {
      type: Object,
      default: () => ({}),
    },
    initFormData: {
      type: Object,
      default: () => ({}),
    },
    showTosInput: {
      type: Boolean,
      default: false,
    },
    buttonText: {
      type: String,
      default: 'Join',
    },
    formAction: {
      type: String,
      default: '/join',
    },
    formClasses: {
      type: String,
      default: 'standard-form',
    },
    showAside: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      processing: false,
      captchaError: false,
      formData: {
        // defaults
        first_name: '',
        last_name: '',
        email: '',
        username: '',
        password: '',
        password_confirmation: '',
        tos: '',
        captcha: '',

        // initial / old form values
        ...this.initFormData,
      },
    }
  },

  computed: {
    initialErrors(): Record<string, string> {
      return errorBagToInitialErrors(this.errorBag)
    },
  },

  // lifecycle methods
  created() {
    // load custom fb* validation rules
    initExtraValidationRules()
  },

  // instance methods
  methods: {
    captchaVerify(response: string) {
      this.formData.captcha = response
      this.captchaError = false
    },

    captchaExpired() {
      this.formData.captcha = ''
      this.captchaError = true
    },

    async handleSubmit(validate: PrivateFormContext['validate']) {
      if (this.processing) {
        return
      }

      // require captcha to be set
      this.captchaError = false

      if (!this.formData.captcha) {
        this.captchaError = true
        return
      }
      // end captcha

      // disable submit
      this.processing = true

      try {
        const results = await validate()

        // invalid, stop
        if (!results.valid) {
          this.processing = false
          return
        }

        // traditional form submit
        if (this.$refs.formRef instanceof HTMLFormElement) {
          // wait until next tick to make sure v-model update
          await this.$nextTick()
          this.$refs.formRef.submit()
        }
      } catch {
        // if error thrown, enable form
        this.processing = false
      }
    },
  },
})
</script>
